import { useState, useEffect } from 'react';
import { 
  Target, Navigation, Plane, Bot, Activity, Radar
} from 'lucide-react';
import CircuitBackground from '../components/CircuitBackground';

export default function Drones() {
  const [missions] = useState([
    { id: 1, name: 'Precision Hover Init', status: 'Ready', type: 'Calibration' },
    { id: 2, name: 'Object Avoidance Sweep', status: 'Locked', type: 'Defense' },
    { id: 3, name: 'Cozmo Self-Awareness', status: 'Experimental', type: 'AI' }
  ]);

  const [activeTab, setActiveTab] = useState<'mission' | 'swarm' | 'cozmo' | 'hud'>('mission');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [hudOpen, setHudOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ minHeight: 'calc(100dvh - 64px)', background: '#050912', color: '#e8f0fe', overflow: 'hidden', position: 'relative' }}>
      <CircuitBackground />
      
      {/* HUD Overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(circle at 50% 50%, transparent 60%, rgba(0,255,136,0.03) 100%)',
        pointerEvents: 'none', zIndex: 1
      }} />

      {/* Main Content */}
      <div style={{ position: 'relative', zIndex: 10, height: 'calc(100dvh - 64px)', display: 'flex', flexDirection: 'column' }}>
        
        {/* Header HUD */}
        <div style={{ 
          padding: '1.5rem', borderBottom: '1px solid rgba(0,255,136,0.2)', 
          background: 'rgba(5,9,18,0.8)', backdropFilter: 'blur(20px)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ 
              width: '40px', height: '40px', background: 'rgba(0,255,136,0.1)', 
              borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(0,255,136,0.3)',
              boxShadow: '0 0 15px rgba(0,255,136,0.2)'
            }}>
              <Plane size={24} color="var(--color-primary)" />
            </div>
            <div>
               <h1 style={{ fontSize: '1.2rem', fontWeight: 800, letterSpacing: '0.05em' }}>SWARM MISSION CONTROL</h1>
               <p style={{ fontSize: '0.7rem', color: 'var(--color-primary)', fontFamily: 'var(--font-mono)' }}>DRTKNG_EDGE_INTELLIGENCE // V1.0</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }} className="desktop-only">
             <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Swarm Health</div>
                <div style={{ color: 'var(--color-primary)', fontSize: '0.85rem', fontWeight: 800 }}>98% ONLINE</div>
             </div>
             <div style={{ width: '1px', background: 'var(--border-color)' }} />
             <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Battery Level</div>
                <div style={{ color: 'var(--color-accent)', fontSize: '0.85rem', fontWeight: 800 }}>82% AVG</div>
             </div>
          </div>

          {isMobile && (
            <button 
              onClick={() => setHudOpen(!hudOpen)}
              style={{
                background: hudOpen ? 'rgba(0,255,136,0.1)' : 'transparent',
                border: '1px solid var(--border-color)',
                borderRadius: '8px', padding: '0.4rem', color: 'var(--color-primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <Radar size={20} />
            </button>
          )}
        </div>

        {/* HUD Info Pop-out for Mobile */}
        {isMobile && hudOpen && (
          <div style={{ 
            padding: '1rem', background: 'rgba(5,9,18,0.95)', borderBottom: '1px solid var(--color-primary)',
            display: 'flex', gap: '2rem', justifyContent: 'center', animation: 'slide-down 0.3s ease-out'
          }}>
             <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Swarm</div>
                <div style={{ color: 'var(--color-primary)', fontSize: '0.8rem', fontWeight: 800 }}>98%</div>
             </div>
             <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Battery</div>
                <div style={{ color: 'var(--color-accent)', fontSize: '0.8rem', fontWeight: 800 }}>82%</div>
             </div>
             <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Latency</div>
                <div style={{ color: 'var(--color-secondary)', fontSize: '0.8rem', fontWeight: 800 }}>14ms</div>
             </div>
          </div>
        )}

        {/* Tab Switcher HUD */}
        <div style={{ 
          display: 'flex', gap: '0.5rem', padding: '0.75rem 1.5rem', 
          background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid var(--border-color)',
          overflowX: 'auto', scrollbarWidth: 'none'
        }}>
           {[
             { id: 'mission', icon: Target, label: 'Missions' },
             { id: 'swarm', icon: Navigation, label: 'Swarm Map' },
             { id: 'cozmo', icon: Bot, label: 'Anki Cozmo Lab' },
             { id: 'hud', icon: Radar, label: 'Tactical HUD' },
           ].map(tab => (
             <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                style={{
                  background: activeTab === tab.id ? 'rgba(0,255,136,0.1)' : 'transparent',
                  border: activeTab === tab.id ? '1px solid var(--color-primary)' : '1px solid transparent',
                  color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--text-muted)',
                  padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                  whiteSpace: 'nowrap', transition: 'all 0.2s'
                }}
             >
               <tab.icon size={16} /> {tab.label}
             </button>
           ))}
        </div>

        {/* Dynamic Realm */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
           
           {activeTab === 'mission' && (
             <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                   {missions.map(m => (
                     <div key={m.id} style={{
                        background: 'var(--bg-surface)', border: '1px solid var(--border-color)',
                        borderRadius: '16px', padding: '1.5rem', position: 'relative', overflow: 'hidden'
                     }}>
                        <div style={{ position: 'absolute', top: 0, right: 0, padding: '0.5rem 1rem', background: m.status === 'Ready' ? 'rgba(0,255,136,0.1)' : 'rgba(255,170,0,0.1)', color: m.status === 'Ready' ? 'var(--color-primary)' : 'var(--color-accent)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', borderRadius: '0 0 0 16px' }}>
                           {m.status}
                        </div>
                        <h3 style={{ marginBottom: '0.5rem' }}>{m.name}</h3>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                           Mission type: {m.type}. Objective: Coordinate edge device telemetry and execute flight paths.
                        </p>
                        <button style={{
                           width: '100%', background: m.status === 'Ready' ? 'var(--color-primary)' : 'rgba(255,255,255,0.05)',
                           color: m.status === 'Ready' ? '#050912' : 'var(--text-muted)', border: 'none',
                           borderRadius: '8px', padding: '0.75rem', fontWeight: 700, cursor: m.status === 'Ready' ? 'pointer' : 'not-allowed'
                        }}>
                           {m.status === 'Ready' ? 'INITIATE MISSION' : 'LOCKED BY CORE'}
                        </button>
                     </div>
                   ))}
                </div>
             </div>
           )}

           {activeTab === 'cozmo' && (
             <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
                <div style={{ 
                  width: '80px', height: '80px', background: 'rgba(0,207,255,0.1)', 
                  borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 1.5rem', border: '1px solid rgba(0,207,255,0.3)',
                  animation: 'float 3s infinite'
                }}>
                  <Bot size={40} color="var(--color-secondary)" />
                </div>
                <h2 style={{ fontSize: '1.8rem', fontWeight: 800, marginBottom: '1rem' }}>Anki Cozmo AI Interface</h2>
                <div style={{ 
                  background: 'rgba(0,207,255,0.05)', border: '1px solid rgba(0,207,255,0.2)', 
                  padding: '2rem', borderRadius: '24px', textAlign: 'left', marginBottom: '2rem'
                }}>
                   <h3 style={{ color: 'var(--color-secondary)', fontSize: '1.1rem', marginBottom: '1rem' }}>Can I load AI on it?</h3>
                   <p style={{ fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                     Cozmo doesn't have the onboard compute for heavy LLMs, but **ElectrAI Bridge** solves this. By using our SDK wrapper, your Cozmo becomes a physical extension of our cloud-based AI.
                   </p>
                   <ul style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', paddingLeft: '1.2rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <li>🤖 **Voice Logic**: Direct ElectrAI reasoning to Cozmo's speaker</li>
                      <li>👁️ **Visual Triage**: Stream Cozmo's camera to our Vision model</li>
                      <li>🎮 **Manual Override**: Playful gamer-mode controls via Web Bluetooth</li>
                      <li>💎 **Gemini Logic**: Seed Cozmo with playful, gamey personalities</li>
                   </ul>
                   <button style={{ 
                     background: 'var(--color-secondary)', color: '#000', border: 'none', 
                     padding: '0.8rem 1.5rem', borderRadius: '10px', fontWeight: 700, cursor: 'pointer',
                     display: 'flex', alignItems: 'center', gap: '0.5rem'
                   }}>
                     DOWNLOAD COZMO_BRIDGE.PY <Activity size={16} />
                   </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                   {[
                     { label: 'Emotion Engine', val: 'HYPER-ACTIVE' },
                     { label: 'Battery Sync', val: 'CONNECTED' },
                     { label: 'Edge Latency', val: '14ms' },
                   ].map(stat => (
                     <div key={stat.label} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', padding: '1rem', borderRadius: '12px' }}>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{stat.label}</div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--color-secondary)' }}>{stat.val}</div>
                     </div>
                   ))}
                </div>
             </div>
           )}

           {activeTab === 'hud' && (
             <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ 
                   width: '300px', height: '300px', border: '2px solid rgba(0,255,136,0.1)', 
                   borderRadius: '50%', position: 'relative',
                   animation: 'spin-slow 20s linear infinite'
                }}>
                   <div style={{ position: 'absolute', top: 0, left: '50%', width: '2px', height: '100%', background: 'rgba(0,255,136,0.1)', transform: 'translateX(-50%)' }} />
                   <div style={{ position: 'absolute', left: 0, top: '50%', width: '100%', height: '2px', background: 'rgba(0,255,136,0.1)', transform: 'translateY(-50%)' }} />
                   <div style={{ position: 'absolute', top: '10%', right: '20%', width: '10px', height: '10px', background: 'var(--color-primary)', borderRadius: '50%', boxShadow: '0 0 15px var(--color-primary)' }} />
                   <div style={{ position: 'absolute', bottom: '30%', left: '10%', width: '8px', height: '8px', background: 'var(--color-accent)', borderRadius: '50%', boxShadow: '0 0 10px var(--color-accent)' }} />
                </div>
                <div style={{ position: 'absolute', bottom: '2rem', right: '2rem', background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.2)', padding: '1rem', borderRadius: '12px', backdropFilter: 'blur(10px)' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Radar size={16} color="var(--color-primary)" />
                      <span style={{ fontSize: '0.75rem', fontWeight: 800 }}>TACTICAL_FEED</span>
                   </div>
                   <div style={{ fontSize: '0.7rem', color: 'var(--color-primary)', fontFamily: 'var(--font-mono)' }}>
                      TARGET_01: ACQUIRED<br/>
                      TARGET_02: SIGNAL_LOSS<br/>
                      SWARM_SYMENTRY: NOMINAL
                   </div>
                </div>
             </div>
           )}

        </div>

        {/* Footer HUD Bar */}
        <div style={{ 
          padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border-color)', 
          background: 'rgba(5,9,18,0.9)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: '0.7rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)'
        }}>
           <div>CORE_CONNECTION: SECURE</div>
           <div style={{ color: 'var(--color-primary)', animation: 'flicker 2s infinite' }}>{'>'} SYNCING_SWARM_COORDINATION_ARRAY...</div>
           <div>COORD: 34.0522° N, 118.2437° W</div>
        </div>

      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .desktop-only { display: flex; }
        @media (max-width: 1024px) {
          .desktop-only { display: none !important; }
        }
      `}</style>
    </div>
  );
}
